import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, Shield, Crown, ChevronRight, LogIn, BookOpen, Sword, ShieldAlert } from 'lucide-react';
import { SupabaseService } from '../services/supabaseService'; // We will replace this soon
import { Profile } from '../types';
import { EloRank } from './EloRank';
import { useAuth } from '../context/AuthContext';

interface TudorProps {
    title?: string;
}

export const TudorHeader: React.FC<TudorProps> = ({ title }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [profile, setProfile] = useState<Profile | null>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const isLanding = location.pathname === '/';

    // Close menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    // Fetch profile
    useEffect(() => {
        if (isMenuOpen) {
            SupabaseService.getProfile().then(setProfile);
        }
    }, [isMenuOpen]);

    const getPageTitle = () => {
        switch ("/" + location.pathname.split("/")[1]) {
            case '/': return '';
            case '/drafting': return 'ARMORY';
            case '/war-room': return 'WAR ROOM';
            case '/battle': return 'BATTLEFIELD';
            case '/campaigns': return 'ROYAL ARCHIVES';
            case '/court': return 'THE COURT';
            case '/barracks': return 'BARRACKS';
            default: return title ?? 'SYSTEM';
        }
    };

    return (
        <header
            className={`
                absolute top-0 left-0 right-0 z-50 transition-all duration-300
                bg-black
                px-6 py-4 flex justify-between items-center
            `}
        >
            {/* LEFT: LOGO & LOCATION */}
            <div className="flex items-center">
                <div
                    onClick={() => navigate('/')}
                    className="cursor-pointer text-tudor-gold hover:text-white transition-colors drop-shadow-md group flex items-center"
                >
                    <img src="./logo.png" style={{ width: '36px', opacity: '0.85' }} />
                </div>

                {!isLanding && (
                    <div className="flex items-center ml-8 animate-fade-in-right">
                        {/* Go Back Arrow */}
                        <button
                            onClick={() => navigate(-1)}
                            className="mr-6 text-tudor-gray hover:text-tudor-gold transition-colors p-1 rounded-full hover:bg-white/5"
                            title="Go Back"
                        >
                            <ChevronRight className="w-5 h-5 rotate-180" />
                        </button>

                        <div className="h-8 w-[1px] bg-tudor-gold/30 mr-6"></div>

                        <span className="text-sm md:text-base tracking-[0.2em] text-tudor-gold/80 font-mono uppercase">
                            {getPageTitle()}
                        </span>
                    </div>
                )}
            </div>

            {/* RIGHT: MENU TOGGLE */}
            <div className="relative">
                <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className={`
                        group flex flex-col items-center justify-center w-10 h-10 gap-1.5 z-50 relative focus:outline-none
                        transition-transform duration-500 ease-in-out
                        ${isMenuOpen ? 'rotate-90' : ''}
                    `}
                    aria-label="Toggle Menu"
                >
                    <span className="w-8 h-0.5 bg-tudor-gold"></span>
                    <span className="w-8 h-0.5 bg-tudor-gold"></span>
                    <span className="w-8 h-0.5 bg-tudor-gold"></span>
                </button>

                {/* DROPDOWN MENU */}
                {isMenuOpen && (
                    <div className="absolute right-0 top-14 w-64 bg-tudor-black/95 border border-tudor-gold shadow-[0_0_30px_rgba(0,0,0,0.9)] backdrop-blur-xl rounded-sm overflow-hidden animate-fade-in-up origin-top-right">
                        {/* User Info */}
                        <div className="p-6 border-b border-tudor-gray/30 bg-tudor-gold/5 flex flex-col items-center text-center">
                            <div className="w-12 h-12 rounded-full bg-tudor-gold/20 border-2 border-tudor-gold flex items-center justify-center mb-3">
                                <User className="text-tudor-gold" />
                            </div>
                            <div className="text-tudor-gold font-bold tracking-widest uppercase text-sm mb-1">
                                {user ? (profile ? (profile.username.startsWith('Commander-') ? 'Commander' : profile.username) : 'Commander') : 'Guest'}
                            </div>

                            {profile && <EloRank rating={profile.rating} size="sm" />}

                            {profile && (
                                <div className={`mt-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${profile.is_premium
                                    ? 'border-tudor-gold text-tudor-gold bg-tudor-gold/10'
                                    : 'border-zinc-700 text-zinc-500'
                                    }`}>
                                    {profile.is_premium ? 'Tribute Active' : 'Silver Status'}
                                </div>
                            )}

                            {/* TREASURY STATUS */}
                            {profile && (
                                <div className={`mt-3 flex items-center gap-2 font-mono text-sm ${profile.gold < 0 ? 'text-red-500' : 'text-tudor-gold'}`}>
                                    {profile.gold < 0 && <ShieldAlert size={14} />}
                                    <span className="font-bold">{profile.gold.toLocaleString()}g</span>
                                    {profile.gold < 0 && <span className="text-[10px] bg-red-900/50 px-1 rounded uppercase">Levy Active</span>}
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="py-2">
                            {user && (
                                <button onClick={() => navigate('/campaigns')} className="w-full text-left px-6 py-3 text-gray-300 hover:bg-tudor-gold/10 hover:text-tudor-gold flex items-center gap-3 transition-colors">
                                    <Shield className="w-4 h-4" />
                                    <span className="uppercase tracking-wider text-xs font-bold">Royal Archives</span>
                                </button>
                            )}

                            <button className="w-full text-left px-6 py-3 text-gray-300 hover:bg-tudor-gold/10 hover:text-tudor-gold flex items-center gap-3 transition-colors">
                                <Settings className="w-4 h-4" />
                                <span className="uppercase tracking-wider text-xs font-bold">Protocols</span>
                            </button>

                            <button onClick={() => navigate('/rules')} className="w-full text-left px-6 py-3 text-gray-300 hover:bg-tudor-gold/10 hover:text-tudor-gold flex items-center gap-3 transition-colors">
                                <BookOpen className="w-4 h-4" />
                                <span className="uppercase tracking-wider text-xs font-bold">The Codex</span>
                            </button>

                            <button onClick={() => navigate('/simulation')} className="w-full text-left px-6 py-3 text-gray-300 hover:bg-tudor-gold/10 hover:text-tudor-gold flex items-center gap-3 transition-colors">
                                <Sword className="w-4 h-4" />
                                <span className="uppercase tracking-wider text-xs font-bold">War Enactment</span>
                            </button>

                            <div className="h-px bg-tudor-gray/20 my-1"></div>

                            {user && !user.email && (
                                <button
                                    onClick={() => navigate('/link-account')}
                                    className="w-full text-left px-6 py-3 text-tudor-gold hover:bg-tudor-gold/10 flex items-center gap-3 transition-colors"
                                >
                                    <ShieldAlert className="w-4 h-4" />
                                    <span className="uppercase tracking-wider text-xs font-bold">Secure Account</span>
                                </button>
                            )}

                            {user ? (
                                <button
                                    onClick={async () => { await signOut(); navigate('/'); }}
                                    className="w-full text-left px-6 py-3 text-red-400 hover:bg-red-900/20 hover:text-red-300 flex items-center gap-3 transition-colors"
                                >
                                    <LogOut className="w-4 h-4" />
                                    <span className="uppercase tracking-wider text-xs font-bold">Resign Command</span>
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate('/auth')}
                                    className="w-full text-left px-6 py-3 text-tudor-gold hover:bg-tudor-gold/10 flex items-center gap-3 transition-colors"
                                >
                                    <LogIn className="w-4 h-4" />
                                    <span className="uppercase tracking-wider text-xs font-bold">Identify</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};
